import { useState, useEffect } from 'react';
import { useAuth } from '@bri/shared-components';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { 
  CogIcon,
  UserIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authService } from '@bri/shared-components';
import { workspaceService } from '@/lib/services/workspaceService';
import { formatWorkspaceRole } from '@/lib/workspaceRoles';
import { getErrorMessage } from '@/lib/utils';
import type { ApiResponse } from '@/types/shared';

export default function SettingsPage() {
  const { user } = useAuth();
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();
  const [isSaving, setIsSaving] = useState(false);
  
  // Workspace settings form state
  const [workspaceName, setWorkspaceName] = useState(currentWorkspace?.name || '');
  const [workspaceDescription, setWorkspaceDescription] = useState(currentWorkspace?.description || '');

  // User profile form state
  const [userName, setUserName] = useState(user?.name || '');
  const userEmail = user?.email || '';

  // Update form state when workspace changes
  useEffect(() => {
    if (currentWorkspace) {
      setWorkspaceName(currentWorkspace.name || '');
      setWorkspaceDescription(currentWorkspace.description || '');
    }
  }, [currentWorkspace]);

  const handleSaveWorkspaceSettings = async () => {
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    if (!workspaceName.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    setIsSaving(true);
    try {
      await workspaceService.updateWorkspace(currentWorkspace.workspaceId, {
        name: workspaceName.trim(),
        description: workspaceDescription.trim() || undefined,
      });
      
      toast.success('Workspace settings saved successfully!');
      
      // Refresh workspaces to get updated data
      await refreshWorkspaces();
    } catch (error: unknown) {
      console.error('Error saving workspace settings:', error);
      toast.error(getErrorMessage(error) || 'Failed to save workspace settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveUserProfile = async () => {
    if (!userName.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsSaving(true);
    try {
      // Call the user profile update API
      const response = await authService.apiRequest('/user/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name: userName.trim(),
        }),
      }) as ApiResponse<Record<string, unknown>>;

      if (response.success) {
        toast.success('Profile updated successfully!');
      } else {
        throw new Error(response.error || 'Failed to update profile');
      }
    } catch (error: unknown) {
      console.error('Error saving user profile:', error);
      toast.error(getErrorMessage(error) || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />
      
      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />
        
        {/* Main content */}
        <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center">
                <CogIcon className="h-8 w-8 text-primary mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                  <p className="text-gray-600 mt-1">Manage your workspace and profile settings</p>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {/* Workspace Settings */}
              {currentWorkspace && (
                <Card className="p-6">
                  <div className="flex items-center mb-6">
                    <BuildingOfficeIcon className="h-6 w-6 text-gray-600 mr-3" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Workspace Settings</h2>
                      <p className="text-gray-600 text-sm">Configure your current workspace</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="workspace-name">Workspace Name</Label>
                      <Input
                        id="workspace-name"
                        type="text"
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        placeholder="Enter workspace name"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="workspace-description">Description (Optional)</Label>
                      <Input
                        id="workspace-description"
                        type="text"
                        value={workspaceDescription}
                        onChange={(e) => setWorkspaceDescription(e.target.value)}
                        placeholder="Enter workspace description"
                        className="mt-1"
                      />
                    </div>

                    <div className="pt-4">
                      <Button
                        onClick={handleSaveWorkspaceSettings}
                        disabled={isSaving}
                        className="w-full sm:w-auto"
                      >
                        {isSaving ? <LoadingSpinner size="sm" /> : null}
                        Save Workspace Settings
                      </Button>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      <strong>Workspace ID:</strong> {currentWorkspace.workspaceId}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      <strong>Your Role:</strong> {formatWorkspaceRole(currentWorkspace.role)}
                    </p>
                  </div>
                </Card>
              )}

              {/* User Profile Settings */}
              <Card className="p-6">
                <div className="flex items-center mb-6">
                  <UserIcon className="h-6 w-6 text-gray-600 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Profile Settings</h2>
                    <p className="text-gray-600 text-sm">Update your personal information</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="user-name">Display Name</Label>
                    <Input
                      id="user-name"
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Enter your name"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="user-email">Email Address</Label>
                    <Input
                      id="user-email"
                      type="email"
                      value={userEmail}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Email address cannot be changed. Contact support if needed.
                    </p>
                  </div>

                  <div className="pt-4">
                    <Button
                      onClick={handleSaveUserProfile}
                      disabled={isSaving}
                      className="w-full sm:w-auto"
                    >
                      {isSaving ? <LoadingSpinner size="sm" /> : null}
                      Save Profile
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
